/* eslint-disable complexity */
import { useContext, useEffect } from 'preact/hooks';
import { createRef } from 'preact';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { POLL_TYPES } from '../../../helpers/constants';

import { PollContext } from '../../../potg/potg.component';
import { enableEditableContent } from '../../../helpers/editable-content';
import PollTextOption from './pollTextOption.component';
import PollImageOption from './pollImageOption.component';
import styleSheet from '../poll.module.scss';

const styles = styleSheet.locals || {};

/**
 * Component for a single poll option
 *
 * @param {object} props poll option props
 * @param {object} props.option poll option data
 * @param {boolean} props.showingResults poll is showing results
 * @param {object} props.transitionStyles transition styles
 * @param {string} props.transitionState transition state
 * @param {number} props.optionsVisibleDelay delay for visibility transition
 * @param {boolean} props.isSelected if option is selected by user
 * @param {boolean} props.isAnswered if option has been chosen as an answer by user
 * @param {Function} props.onSelect callback when option has been selected by user
 * @param {number} props.optionImageWidth image width for ODIR
 * @param {number} props.index index of option in optionList
 * @returns {Function} <PollOption />
 */
function PollOption({
    option,
    showingResults = false,
    transitionStyles,
    transitionState,
    optionsVisibleDelay,
    isSelected = false,
    isAnswered = false
}) {
    const {
        poll,
        selectedOptions,
        pollOpen,
        pollUserAnswer = null,
        showVotesCounter = false,
        isPreview,
        hasOptionImages,
        onPreviewEditChange
    } = useContext(PollContext);
    const optionTextRef = createRef();

    const changeEditableData = (field, value) => {
        onPreviewEditChange?.({
            questions: [
                {
                    options: [{ ...option, [field]: value }]
                }
            ]
        });
    };

    useEffect(() => {
        if (isPreview) {
            enableEditableContent(
                optionTextRef.current,
                'text',
                changeEditableData
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!option) {
        return '';
    }

    const maxSelection = poll?.questions?.[0]?.options_selection_limit ?? 1;

    // Poll
    const activationClasses = classNames(styles.pollOption, {
        [styles.pollOptionLarge]: !hasOptionImages,
        [styles.pollOptionNoHover]: !pollOpen || pollUserAnswer,
        [styles.pollOptionSelected]: isSelected || isAnswered,
        [styles.pollOptionDisabled]:
            !pollUserAnswer &&
            !isSelected &&
            selectedOptions.length === maxSelection,
        [styles.pollOptionEnded]: !pollOpen || pollUserAnswer,
        [styles.pollOptionResults]: showingResults,
        [styles.pollOptionSubmitted]:
            !pollOpen && showingResults && (isSelected || isAnswered),
        [styles.pollOptionBig]:
            ((pollUserAnswer && pollOpen) || !pollOpen) &&
            showVotesCounter &&
            !hasOptionImages,
        [styles.pollOptionImage]: hasOptionImages
    });

    return (
        <div part="potg-option" className={activationClasses}>
            {poll.type === POLL_TYPES.PLAYER_OF_THE_GAME &&
                (hasOptionImages ? (
                    <PollImageOption
                        option={option}
                        isSelected={isSelected}
                        isAnswered={isAnswered}
                        showingResults={showingResults}
                        transitionStyles={transitionStyles}
                        transitionState={transitionState}
                        optionsVisibleDelay={optionsVisibleDelay}
                        optionTextRef={optionTextRef}
                    />
                ) : (
                    <PollTextOption
                        option={option}
                        isSelected={isSelected}
                        isAnswered={isAnswered}
                        showingResults={showingResults}
                        transitionStyles={transitionStyles}
                        transitionState={transitionState}
                        optionsVisibleDelay={optionsVisibleDelay}
                        optionTextRef={optionTextRef}
                    />
                ))}
        </div>
    );
}

PollOption.propTypes = {
    option: PropTypes.object.isRequired,
    showingResults: PropTypes.bool,
    transitionStyles: PropTypes.object,
    transitionState: PropTypes.string,
    optionsVisibleDelay: PropTypes.number,
    isSelected: PropTypes.bool,
    isAnswered: PropTypes.bool
};

export default PollOption;
